import { TriggerKey, TriggerType } from '../userCommunicationAgent';

/** AI-simulated trigger events used to test EMAIL-, SLACK-, and DOBOX-triggered flows before they go live. */
export enum FlowScenarioKind {
  HAPPY_PATH = 'HAPPY_PATH',
  EDGE_CASE = 'EDGE_CASE',
  NON_TRIGGERING = 'NON_TRIGGERING',
}

/** The subset of TriggerType that scenario simulation currently supports. */
export type ScenarioTriggerType =
  | TriggerType.EMAIL
  | TriggerType.SLACK
  | TriggerType.DOBOX;

export interface SyntheticEmailFields {
  senderName: string;
  senderEmail: string;
  subject: string;
  body: string;
}

export interface SyntheticSlackFields {
  authorName: string;
  /** Undefined when the trigger doesn't fix an author and none could be resolved from the cache. */
  authorSlackId?: string;
  message: string;
  workspaceId: string;
  workspaceName?: string;
  channelId?: string;
  channelName?: string;
}

/**
 * DOBOX scenarios never build a transient UserCommunication — they feed the
 * DoBox seed context directly (see `buildSyntheticDoboxSeedContext`).
 * `taskGroupId`/`taskLabelIds` are resolved deterministically (never by the
 * model) via `apps/api/src/utils/doboxScenarioIds.ts`; the rest is content.
 */
export interface SyntheticDoboxFields {
  /** One of the 8 DOBOX_TRIGGER_KEYS — must match the flow's own trigger key. */
  triggerKey: TriggerKey;
  subject: string;
  description?: string;
  taskGroupId: string;
  taskGroupName?: string;
  taskLabelIds?: string[];
  taskLabelNames?: string[];
  /** COMMENT_ADDED only. */
  commentMessage?: string;
  commentAuthor?: string;
  /** PARTICIPANT_ADDED only. */
  addedParticipantNames?: string[];
  /** LABEL_ADDED only. */
  addedLabelNames?: string[];
  /** LABEL_REMOVED only. */
  removedLabelNames?: string[];
  /** TASK_GROUP_CHANGED only. */
  newTaskGroupId?: string;
  newTaskGroupName?: string;
  dueAt?: string;
}

/**
 * A single AI-generated candidate communication, along with why it was built
 * that way. Exactly one of `email`/`slack`/`dobox` is populated, matching `platform`.
 */
export interface SyntheticTriggerScenario {
  kind: FlowScenarioKind;
  platform: ScenarioTriggerType;
  rationale: string;
  email?: SyntheticEmailFields;
  slack?: SyntheticSlackFields;
  dobox?: SyntheticDoboxFields;
}

/**
 * What a synthetic flow run actually consumes — the scenario minus
 * kind/rationale. Exactly one of `email`/`slack`/`dobox` is populated, matching `platform`.
 */
export interface SyntheticCommunicationInput {
  platform: ScenarioTriggerType;
  email?: SyntheticEmailFields;
  slack?: SyntheticSlackFields;
  dobox?: SyntheticDoboxFields;
}

/**
 * messageId prefix for transient synthetic communications built from a
 * SyntheticCommunicationInput. Never written to UserCommunications — this
 * prefix lets the read path (attachment extraction) recognize and skip them.
 */
export const SYNTHETIC_COMMUNICATION_MESSAGE_ID_PREFIX = 'synthetic-';
